import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  AlertCircle,
  AlertTriangle,
  Building2,
  Pencil,
  Info,
  FileText,
  MapPin,
  MapPinned,
  Map,
  Phone,
  Mail,
  Globe,
  Search,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { authenticatedApi, MEDIA_URL } from "@/services/api";
import MastersLayout from "@/components/masters/MastersLayout";

type Organization = {
  id: string;
  name: string;
  aliasName: string | null;
  buildingName: string | null;
  streetName: string;
  city: string;
  subDivision: string | null;
  postalCode: string;
  state: string;
  country: string;
  location: string | null;
  contactNo: string;
  email: string | null;
  website: string | null;
  companyRegistrationNumber: string | null;
  taxRegistrationNumber: string | null;
  logo: string | null;
  status: string;
  createdAt?: string;
  updatedAt?: string;
};

type UserProfile = {
  id: string;
  organizationId: string;
  name: string;
  email: string;
  password?: string;
  image: string | null;
  mobile: string;
  whatsApp: string | null;
  fcm: string | null;
  lastLogin: string | null;
  role: string;
  isActive: boolean;
  categoryId: string | null;
  zoneId: string | null;
  divisionId: string | null;
  areaId: string | null;
  createdAt: string;
  updatedAt: string;
};

const INITIAL_ORGANIZATION: Organization = {
  id: "",
  name: "",
  aliasName: "",
  buildingName: "",
  streetName: "",
  city: "",
  subDivision: "",
  postalCode: "",
  state: "",
  country: "",
  location: "",
  contactNo: "",
  email: "",
  website: "",
  companyRegistrationNumber: "",
  taxRegistrationNumber: "",
  logo: "",
  status: "verified",
};

// Website validation regex
const websiteRegex =
  /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

type AuthError = { status: number; message: string; isAuthError?: boolean };
function isAuthError(error: unknown): error is AuthError {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status?: number }).status === 401 &&
    "isAuthError" in error &&
    (error as { isAuthError?: boolean }).isAuthError === true
  );
}

const OrganizationPage = () => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Organization>(INITIAL_ORGANIZATION);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [showLogoPreview, setShowLogoPreview] = useState(false);
  const { toast } = useToast();

  // Load user profile and organization data when component mounts
  useEffect(() => {
    const loadProfileAndOrganization = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("manterflow-token");
        if (!token) {
          setError("Authentication token not found. Please log in again.");
          setLoading(false);
          return;
        }

        const storedProfile = localStorage.getItem("manterflow-user-profile");
        const storedOrg = localStorage.getItem("manterflow-organization");
        let profileData = null;

        if (storedProfile) {
          try {
            profileData = JSON.parse(storedProfile);
            setUserProfile(profileData);
            console.log("Loaded user profile from localStorage");
          } catch (e) {
            console.error("Error parsing stored profile", e);
            profileData = null;
          }
        }

        if (storedOrg) {
          try {
            const orgData = JSON.parse(storedOrg);
            setOrganization(orgData);
            setFormData(orgData);
            setLogoPreview(orgData.logo || "");
            console.log("Loaded organization data from localStorage");
          } catch (e) {
            console.error("Error parsing organization data", e);
          }
        }

        // Always fetch latest organization data from API if we have profileData
        console.log("profileData", profileData);
        if (profileData?.organizationId) {
          try {
            const orgResponse = await authenticatedApi.getOrganization(
              profileData.organizationId
            );
            if (orgResponse && orgResponse.data) {
              setOrganization(orgResponse.data);
              setFormData(orgResponse.data);
              setLogoPreview(orgResponse.data.logo || "");
              localStorage.setItem(
                "manterflow-organization",
                JSON.stringify(orgResponse.data)
              );
              console.log("Fetched and stored organization data from API");
            }
          } catch (orgError) {
            if (isAuthError(orgError)) {
              toast({
                title: "Session expired",
                description: orgError.message,
                variant: "destructive",
              });
              setTimeout(() => {
                window.location.href = "/login";
              }, 1500);
              return;
            } else {
              console.error("Error fetching organization data:", orgError);
            }
          }
        }

        if (!profileData) {
          console.log("No profile data in localStorage, fetching from API");
          try {
            const profileResponse = await authenticatedApi.getProfile();
            if (profileResponse && profileResponse.data) {
              setUserProfile(profileResponse.data);
              profileData = profileResponse.data;

              localStorage.setItem(
                "manterflow-user-profile",
                JSON.stringify(profileResponse.data)
              );
              console.log("Fetched and stored user profile from API");
            }
          } catch (profileError: unknown) {
            if (isAuthError(profileError)) {
              toast({
                title: "Session expired",
                description: profileError.message,
                variant: "destructive",
              });
              setTimeout(() => {
                window.location.href = "/login";
              }, 1500);
              return;
            } else {
              console.error("Error fetching profile data:", profileError);
            }
          }
        }

        if (profileData?.organizationId && !storedOrg) {
          console.log(
            "Fetching organization data using organizationId:",
            profileData.organizationId
          );
          try {
            const orgResponse = await authenticatedApi.getOrganization(
              profileData.organizationId
            );
            if (orgResponse && orgResponse.data) {
              setOrganization(orgResponse.data);
              setFormData(orgResponse.data);
              setLogoPreview(orgResponse.data.logo || "");

              localStorage.setItem(
                "manterflow-organization",
                JSON.stringify(orgResponse.data)
              );
              console.log("Fetched and stored organization data from API");
            }
          } catch (orgError: unknown) {
            if (isAuthError(orgError)) {
              toast({
                title: "Session expired",
                description: orgError.message,
                variant: "destructive",
              });
              setTimeout(() => {
                window.location.href = "/login";
              }, 1500);
              return;
            } else {
              console.error("Error fetching organization data:", orgError);
            }
          }
        }
      } catch (error: unknown) {
        if (isAuthError(error)) {
          toast({
            title: "Session expired",
            description: error.message,
            variant: "destructive",
          });
          setTimeout(() => {
            window.location.href = "/login";
          }, 1500);
          return;
        } else {
          console.error("Error in loadProfileAndOrganization:", error);
          setError(
            typeof error === "object" && error && "message" in error
              ? (error as { message?: string }).message ||
                  "Failed to load profile and organization data"
              : "Failed to load profile and organization data"
          );
        }
      } finally {
        setLoading(false);
      }
    };

    loadProfileAndOrganization();
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });

    // Validate website field if it's being changed
    if (field === "website") {
      const trimmedValue = value.trim();
      if (trimmedValue && !websiteRegex.test(trimmedValue)) {
        setWebsiteError(
          "Please enter a valid website URL (e.g., https://example.com)"
        );
      } else {
        setWebsiteError(null);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organization || !userProfile) {
      toast({
        title: "Error",
        description: "Organization or user data not available",
        variant: "destructive",
      });
      return;
    }

    // Validate required fields
    const requiredFields: (keyof Organization)[] = [
      "name",
      "streetName",
      "city",
      "postalCode",
      "state",
      "country",
      "contactNo",
    ];
    for (const field of requiredFields) {
      if (!formData[field] || !formData[field].toString().trim()) {
        toast({
          title: "Validation Error",
          description: `${field
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())} is required`,
          variant: "destructive",
        });
        return;
      }
    }

    // Check for website validation error
    if (websiteError) {
      toast({
        title: "Validation Error",
        description: websiteError,
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      console.log("Submitting organization update with data:", formData);

      // Prepare payload with trimmed values for string fields
      const trimmedFormData = { ...formData };
      Object.keys(trimmedFormData).forEach((key) => {
        if (typeof trimmedFormData[key] === "string") {
          trimmedFormData[key] = trimmedFormData[key].trim();
        }
      });
      const response = await authenticatedApi.updateOrganization(
        organization.id,
        trimmedFormData
      );

      console.log("Organization update response:", response);

      setShowForm(false);

      if (response && response.data) {
        toast({
          title: "Success",
          description: "Organization details updated successfully",
        });

        try {
          console.log("Fetching updated organization data...");
          await fetchOrganizationData(organization.id);
        } catch (fetchError) {
          console.error(
            "Error fetching updated organization data:",
            fetchError
          );
        }
      } else {
        toast({
          title: "Warning",
          description: "Update completed but no data returned",
        });
      }
    } catch (error: any) {
      console.error("Error updating organization:", error);
      toast({
        title: "Update failed",
        description: error.message || "Failed to update organization details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationData = async (organizationId: string) => {
    try {
      setLoading(true);
      console.log(`Fetching organization data for ID: ${organizationId}`);

      if (!organizationId) {
        throw new Error("Organization ID is required to fetch data");
      }

      const orgResponse = await authenticatedApi.getOrganization(
        organizationId
      );
      console.log("Organization data response:", orgResponse);

      if (orgResponse && orgResponse.data) {
        setOrganization(orgResponse.data);
        setFormData(orgResponse.data);

        if (orgResponse.data.logo) {
          setLogoPreview(orgResponse.data.logo);
        }

        localStorage.setItem(
          "manterflow-organization",
          JSON.stringify(orgResponse.data)
        );

        console.log("Organization data updated successfully");
        return orgResponse.data;
      } else {
        throw new Error("No organization data returned from API");
      }
    } catch (error: any) {
      console.error("Error fetching organization data:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch organization data",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Handler to allow only numeric input
  const handleNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/[0-9]/.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "Tab" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight"
    ) {
      e.preventDefault();
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      console.log("Uploading file:", file.name);

      const formData = new FormData();
      formData.append("files", file);

      const uploadResponse = await authenticatedApi.uploadFiles(formData);
      console.log("File upload response:", uploadResponse);

      if (
        uploadResponse &&
        uploadResponse.data &&
        uploadResponse.data.length > 0
      ) {
        const filePath = uploadResponse.data[0];
        console.log("File path from upload:", filePath);

        setFormData((prevData) => ({
          ...prevData,
          logo: filePath,
        }));

        toast({
          title: "Success",
          description: "Logo uploaded successfully",
        });
      } else {
        throw new Error("No file path returned from upload");
      }
    } catch (error: any) {
      console.error("Error uploading logo:", error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload logo",
        variant: "destructive",
      });

      e.target.value = "";
    } finally {
      setLoading(false);
    }
  };

  console.log("organization", organization);

  return (
    <MastersLayout>
      <div className="space-y-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground font-medium">
              Loading organization data...
            </span>
          </div>
        ) : error ? (
          <div className="p-6 bg-red-50 dark:bg-red-950/20 text-red-800 dark:text-red-300 rounded-lg shadow-sm">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Error
            </h3>
            <p className="mt-1">{error}</p>
          </div>
        ) : !organization ? (
          <div className="p-6 bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300 rounded-lg shadow-sm">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              No Data
            </h3>
            <p className="mt-1">No organization data available.</p>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto">
            {/* Header with logo and organization name */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="relative">
                  {organization.logo ? (
                    <div
                      className="cursor-pointer group relative"
                      // onClick={() => setShowLogoPreview(true)}
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden shadow-md bg-white dark:bg-slate-800 flex items-center justify-center border border-slate-200 dark:border-slate-700">
                        <img
                          src={
                            organization.logo
                              ? `${MEDIA_URL}${organization.logo}`
                              : "/placeholder.svg"
                          }
                          alt="Logo"
                          className="max-w-full max-h-full object-contain p-1"
                        />
                      </div>
                      {/* <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 dark:group-hover:bg-white/10 rounded-lg transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <Search className="w-5 h-5 text-white drop-shadow-md" />
                      </div> */}
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center border border-slate-200 dark:border-slate-700">
                      <Building2 className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                </div>

                <div>
                  <h1 className="text-2xl font-bold tracking-tight">
                    {organization.name}
                  </h1>
                  <p className="text-muted-foreground">
                    {organization?.aliasName?.trim() ||
                      [organization?.city, organization?.country]
                        .filter(Boolean)
                        .join(", ") ||
                      "--"}
                  </p>
                </div>
              </div>

              <Button onClick={() => setShowForm(true)} className="gap-2">
                <Pencil className="h-4 w-4" />
                <span className="hidden sm:inline">Edit Organization</span>
              </Button>
            </div>

            {/* Content sections */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Basic Information Card */}
              <Card className="shadow-sm border-slate-200 dark:border-slate-800 overflow-hidden">
                <CardHeader className="bg-slate-50 dark:bg-slate-900/50 pb-4">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">
                      Basic Information
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <dl className="space-y-4">
                    <div className="flex flex-col">
                      <dt className="text-sm font-medium text-muted-foreground">
                        Organization Name
                      </dt>
                      <dd className="mt-1">{organization.name}</dd>
                    </div>

                    <div className="flex flex-col">
                      <dt className="text-sm font-medium text-muted-foreground">
                        Alias Name
                      </dt>
                      <dd className="mt-1">{organization.aliasName || "-"}</dd>
                    </div>

                    <div className="flex flex-col">
                      <dt className="text-sm font-medium text-muted-foreground">
                        Contact Number
                      </dt>
                      <dd className="mt-1 flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                        {organization.contactNo}
                      </dd>
                    </div>

                    <div className="flex flex-col">
                      <dt className="text-sm font-medium text-muted-foreground">
                        Email Address
                      </dt>
                      <dd className="mt-1 flex items-center gap-2">
                        {organization.email ? (
                          <>
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                            {organization.email}
                          </>
                        ) : (
                          "-"
                        )}
                      </dd>
                    </div>

                    <div className="flex flex-col">
                      <dt className="text-sm font-medium text-muted-foreground">
                        Website
                      </dt>
                      <dd className="mt-1">
                        {organization.website ? (
                          <a
                            href={
                              organization.website.startsWith("http")
                                ? organization.website
                                : `https://${organization.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline flex items-center gap-2"
                          >
                            <Globe className="h-3.5 w-3.5" />
                            Visit Website
                          </a>
                        ) : (
                          "-"
                        )}
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>

              {/* Registration Information Card */}
              <Card className="shadow-sm border-slate-200 dark:border-slate-800 overflow-hidden">
                <CardHeader className="bg-slate-50 dark:bg-slate-900/50 pb-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">
                      Registration Information
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <dl className="space-y-4">
                    <div className="flex flex-col">
                      <dt className="text-sm font-medium text-muted-foreground">
                        Company Registration Number
                      </dt>
                      <dd className="mt-1">
                        {organization.companyRegistrationNumber || "-"}
                      </dd>
                    </div>

                    <div className="flex flex-col">
                      <dt className="text-sm font-medium text-muted-foreground">
                        Tax Registration Number
                      </dt>
                      <dd className="mt-1">
                        {organization.taxRegistrationNumber || "-"}
                      </dd>
                    </div>

                    <div className="flex flex-col">
                      <dt className="text-sm font-medium text-muted-foreground">
                        Status
                      </dt>
                      <dd className="mt-1">
                        <Badge
                          variant="outline"
                          className={`${
                            organization.status === "active"
                              ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-900"
                              : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900"
                          }`}
                        >
                          {organization.status === "active"
                            ? "Active"
                            : organization.status}
                        </Badge>
                      </dd>
                    </div>

                    {organization.createdAt && (
                      <div className="flex flex-col">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Registered On
                        </dt>
                        <dd className="mt-1 text-sm">
                          {new Date(organization.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </dd>
                      </div>
                    )}
                  </dl>
                </CardContent>
              </Card>

              {/* Address Information Card */}
              <Card className="shadow-sm border-slate-200 dark:border-slate-800 overflow-hidden">
                <CardHeader className="bg-slate-50 dark:bg-slate-900/50 pb-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">
                      Address Information
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <dl className="space-y-4">
                    <div className="flex flex-col">
                      <dt className="text-sm font-medium text-muted-foreground">
                        Full Address
                      </dt>
                      <dd className="mt-1">
                        <address className="not-italic">
                          {organization.buildingName && (
                            <div>{organization.buildingName},</div>
                          )}
                          <div>{organization.streetName},</div>
                          <div>
                            {organization.city},{" "}
                            {organization.subDivision || ""}
                          </div>
                          <div>
                            {organization.state}, {organization.country}{" "}
                            {organization.postalCode}
                          </div>
                        </address>
                      </dd>
                    </div>

                    {organization.location && (
                      <div className="flex flex-col">
                        <dt className="text-sm font-medium text-muted-foreground">
                          Location Coordinates
                        </dt>
                        <dd className="mt-1 flex items-center gap-2">
                          <MapPinned className="h-3.5 w-3.5 text-muted-foreground" />
                          {organization.location}
                        </dd>
                      </div>
                    )}

                    {/* <div className="pt-2">
                      <Button variant="outline" size="sm" className="w-full gap-2" disabled>
                        <Map className="h-4 w-4" />
                        View on Map
                      </Button>
                    </div> */}
                  </dl>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Logo Preview Dialog */}
      {organization?.logo && (
        <Dialog open={showLogoPreview} onOpenChange={setShowLogoPreview}>
          <DialogContent className="max-w-3xl">
            <div className="flex justify-center items-center p-4">
              <img
                src={`${MEDIA_URL}${organization.logo}`}
                alt="Organization Logo"
                className="max-w-full max-h-[80vh] object-contain"
                style={{ background: "#fff" }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showForm && (
        <Dialog
          open={showForm}
          onOpenChange={(open) => {
            if (!open) {
              setShowForm(false);
              setFormData(organization!);
              setLogoPreview(organization!.logo || "");
              setWebsiteError(null);
            }
          }}
        >
          <DialogContent className="max-w-3xl w-[95vw] p-4 sm:p-6 overflow-y-auto max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Edit Organization</DialogTitle>
              <DialogDescription>
                Update your organization details
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 py-4 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Organization Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="Enter organization name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="aliasName">Alias Name</Label>
                    <Input
                      id="aliasName"
                      value={formData.aliasName || ""}
                      onChange={(e) =>
                        handleInputChange("aliasName", e.target.value)
                      }
                      placeholder="Enter alias name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="buildingName">Building Name</Label>
                    <Input
                      id="buildingName"
                      value={formData.buildingName || ""}
                      onChange={(e) =>
                        handleInputChange("buildingName", e.target.value)
                      }
                      placeholder="Enter building name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="streetName">Street Name</Label>
                    <Input
                      id="streetName"
                      value={formData.streetName}
                      onChange={(e) =>
                        handleInputChange("streetName", e.target.value)
                      }
                      placeholder="Enter street name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      placeholder="Enter city"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subDivision">Sub Division</Label>
                    <Input
                      id="subDivision"
                      value={formData.subDivision || ""}
                      onChange={(e) =>
                        handleInputChange("subDivision", e.target.value)
                      }
                      placeholder="Enter sub division name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="postalCode">Postal Code</Label>
                    <Input
                      id="postalCode"
                      value={formData.postalCode}
                      onChange={(e) =>
                        handleInputChange("postalCode", e.target.value)
                      }
                      placeholder="Enter postal code"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) =>
                        handleInputChange("state", e.target.value)
                      }
                      placeholder="Enter state"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) =>
                        handleInputChange("country", e.target.value)
                      }
                      placeholder="Enter country"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location || ""}
                      onChange={(e) =>
                        handleInputChange("location", e.target.value)
                      }
                      placeholder="Enter location"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contactNo">Contact No</Label>
                    <Input
                      id="contactNo"
                      type="tel"
                      value={formData.contactNo}
                      onKeyDown={handleNumericInput}
                      onChange={(e) =>
                        handleInputChange("contactNo", e.target.value)
                      }
                      placeholder="Enter contact number"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email || ""}
                      // onChange={(e) =>
                      //   handleInputChange("email", e.target.value)
                      // }
                      disabled={true}
                      // placeholder="Enter email address"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={formData.website || ""}
                      onChange={(e) =>
                        handleInputChange("website", e.target.value)
                      }
                      placeholder="Enter website URL (e.g., https://example.com)"
                    />
                    {websiteError && (
                      <p className="text-sm text-destructive">{websiteError}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyRegistrationNumber">
                      Company Registration No
                    </Label>
                    <Input
                      id="companyRegistrationNumber"
                      value={formData.companyRegistrationNumber || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "companyRegistrationNumber",
                          e.target.value
                        )
                      }
                      placeholder="Enter company registration number"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="taxRegistrationNumber">
                      Tax Registration No
                    </Label>
                    <Input
                      id="taxRegistrationNumber"
                      value={formData.taxRegistrationNumber || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "taxRegistrationNumber",
                          e.target.value
                        )
                      }
                      placeholder="Enter tax registration number"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="logo">Logo</Label>
                    <div className="flex items-center gap-4">
                      <div className="relative h-20 w-20 overflow-hidden rounded-full group bg-gray-100 flex items-center justify-center">
                        <img
                          src={
                            logoPreview
                              ? logoPreview.startsWith("data:")
                                ? logoPreview
                                : `${MEDIA_URL}${logoPreview}`
                              : "/placeholder.svg"
                          }
                          alt="Logo Preview"
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            document.getElementById("org-logo-input")?.click()
                          }
                          className="absolute bottom-1 right-1 bg-primary text-white rounded-full p-1 shadow-md border border-white flex items-center justify-center hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                          style={{ width: 32, height: 32 }}
                          tabIndex={0}
                          aria-label="Upload organization logo"
                        >
                          <span className="text-lg leading-none">+</span>
                        </button>
                        <input
                          id="org-logo-input"
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="hidden"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  type="button"
                  className="w-full sm:w-auto order-2 sm:order-1"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="w-full sm:w-auto order-1 sm:order-2"
                  disabled={
                    loading ||
                    (formData.website && websiteError !== null) ||
                    !formData.name ||
                    !formData.streetName ||
                    !formData.city ||
                    !formData.postalCode ||
                    !formData.state ||
                    !formData.country ||
                    !formData.contactNo
                  }
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </MastersLayout>
  );
};

export default OrganizationPage;
